import { useCallback } from "react";

import { atom, useAtom, PrimitiveAtom } from "jotai";
import {
  atomFamily,
  atomWithStorage,
  useAtomValue,
  useUpdateAtom,
} from "jotai/utils";

import { Api } from "interfaces";

import * as flowConfig from "flows/driver-analysis-continous/config";

const inputFile = atomWithStorage<string>(
  `${flowConfig.FLOW_NAME}/create/input-file`,
  null
);

export const useInputFile = () => useAtom(inputFile);

export const useGetInputFile = () => useAtomValue(inputFile);

const outcome = atomFamily((fileId: string) =>
  atomWithStorage<string>(
    `${flowConfig.FLOW_NAME}/create/${fileId}/outcomes`,
    null
  )
);

export const useOutcome = () => {
  const fileId = useGetInputFile();
  return useAtom(outcome(fileId || ""));
};

export const useGetOutcome = () => {
  const fileId = useGetInputFile();
  return useAtomValue(outcome(fileId || ""));
};

const projectCreateStatus = atom<"not-started" | "in-progress" | "completed">(
  "not-started"
);

export const useCreateStatus = () => useAtom(projectCreateStatus);

export const useGetCreateStatus = () => useAtomValue(projectCreateStatus);

const projectCreateError = atom<string | Api.ApiError>(null) as PrimitiveAtom<
  string | Api.ApiError
>;

export const useCreateError = () => useAtom(projectCreateError);

export const useGetCreateError = () => useAtomValue(projectCreateError);

const createSpec = atom((get) => {
  const currentFile = get(inputFile);
  if (!currentFile) {
    return null;
  }
  const y = get(outcome(currentFile));
  if (!y) {
    return null;
  }

  return {
    flow: flowConfig.FLOW_NAME,
    data_files: { [flowConfig.INPUT_DATA]: currentFile },
    meta_data: { outcomes: [y] },
  };
});

export const useGetCreateSpec = () => useAtomValue(createSpec);

export const useResetStatus = () => {
  const setStatus = useUpdateAtom(projectCreateStatus);
  const setError = useUpdateAtom(projectCreateError);

  return useCallback(() => {
    setStatus("not-started");
    setError(null);
  }, [setStatus, setError]);
};
